'use client'
import { useTranslation } from 'react-i18next'
import { InfoButton } from '@/features/priceCalculator/PurchaseForm/OfferPresenter/InfoButton/InfoButton'
import { useTierComparison } from '@/features/tierComparison/TierComparisonProvider'
import { tierComparisonToTable } from '@/features/tierComparison/tierComparisonToTable'
import { useResponsiveVariant } from '@/utils/useResponsiveVariant'
import { DesktopComparisonTable } from 'ui/src/components/ComparisonTable/DesktopComparisonTable'
import { MobileComparisonTable } from 'ui/src/components/ComparisonTable/MobileComparisonTable'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'

type Props = {
  title: string
  subtitle?: string
  selectedTermsVersion?: string
  showCoverageText?: boolean
}

export function TierComparisonDialog({ title, subtitle, selectedTermsVersion, showCoverageText }: Props) {
  const variant = useResponsiveVariant('sm')
  const tierComparison = useTierComparison()
  const { t } = useTranslation(['common', 'purchase-form'])
  const { table, selectedTierDisplayName } = tierComparisonToTable(tierComparison, {
    t,
    selectedTermsVersion,
    showCoverageText,
  })

  return (
    <LargeDialog.Root>
      <Dialog.Trigger asChild>
        <InfoButton>
          <div className={yStack()}>
            <Text size="xs">{t('purchase-form:COMPARE_COVERAGE_DIALOG_TRIGGER')}</Text>
            <Text color="textSecondary" size="xs" balance={true}>
              {t('purchase-form:COMPARE_COVERAGE_DIALOG_TRIGGER_DESCRIPTION')}
            </Text>
          </div>
        </InfoButton>
      </Dialog.Trigger>

      <LargeDialog.Content>
        <LargeDialog.CloseIconButton aria-label={t('DIALOG_CLOSE')} />
        <LargeDialog.Heading as="h2">{title}</LargeDialog.Heading>
        {subtitle && (
          <LargeDialog.Heading as="h3" color="textSecondary" className={sprinkles({ marginBottom: 'md' })}>
            {subtitle}
          </LargeDialog.Heading>
        )}
        <>
          {variant === 'mobile' && (
            <MobileComparisonTable
              className={sprinkles({ paddingBottom: 'lg' })}
              {...table}
              defaultSelectedColumn={selectedTierDisplayName}
            />
          )}
          {variant === 'desktop' && <DesktopComparisonTable {...table} selectedColumn={selectedTierDisplayName} />}
        </>
      </LargeDialog.Content>
    </LargeDialog.Root>
  )
}
